const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Cookie-only auth (P1-3): tokens live in httpOnly cookies set by the
 * server. JS never touches them. We only read the non-httpOnly `csrf_token`
 * cookie for double-submit (P1-4).
 */

function readCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[-.+*]/g, '\\$&') + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

// Legacy shim — older code calls these. Now no-ops because cookies are the
// source of truth. Keep exports so imports don't break.
export function setTokens() { /* deprecated: cookies are httpOnly */ }
export function clearTokens() { /* deprecated: server clears cookies on /logout */ }
export function getAccessToken() { return null; }

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const UNSAFE = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// Refresh-token race fix: only ever have ONE /api/auth/refresh in flight.
// Concurrent 401s would otherwise each fire their own refresh, race the
// token_version bump, and trigger the reuse-detection cascade that kills
// the session. With this dedupe, the 2nd..Nth callers await the same
// pending promise and reuse its result.
let _pendingRefresh = null;
async function _refreshOnce() {
  if (_pendingRefresh) return _pendingRefresh;
  _pendingRefresh = (async () => {
    try {
      const r = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      return r.ok;
    } catch {
      return false;
    } finally {
      // Release the lock on the next microtask so all parallel callers
      // observe the same outcome before a fresh refresh can start.
      setTimeout(() => { _pendingRefresh = null; }, 0);
    }
  })();
  return _pendingRefresh;
}

function buildHeaders(method, extra = {}) {
  const headers = { 'Content-Type': 'application/json', ...extra };
  // P1-4: double-submit CSRF token for unsafe methods.
  if (UNSAFE.has(method)) {
    const csrf = readCookie('csrf_token');
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }
  return headers;
}

async function apiRequest(path, options = {}) {
  const { method = 'GET', body, headers = {} } = options;
  const upper = method.toUpperCase();
  const config = {
    method: upper,
    credentials: 'include',
    headers: buildHeaders(upper, headers),
  };
  if (body) config.body = JSON.stringify(body);

  let res = await fetch(`${API_URL}${path}`, config);

  // Auto-refresh on 401 (cookie expired) — uses a deduped, single-flight
  // refresh so parallel 401s don't race each other and trigger the
  // reuse-detection cascade.
  if (res.status === 401 && path !== '/api/auth/refresh' && path !== '/api/auth/me') {
    const ok = await _refreshOnce();
    if (ok) {
      // New cookies (incl. fresh csrf_token) are now set. Retry once.
      res = await fetch(`${API_URL}${path}`, {
        ...config,
        headers: buildHeaders(upper, headers),
      });
    }
  }

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    if (res.status === 402) {
      try {
        const detail = typeof data.detail === 'string' ? data.detail : 'Pro feature';
        window.dispatchEvent(new CustomEvent('upgrade-required', { detail: { message: detail } }));
      } catch {}
    }
    throw new Error(formatApiErrorDetail(data.detail));
  }
  return res.json();
}

async function uploadFile(path, file) {
  const formData = new FormData();
  formData.append('file', file);
  const headers = {};
  const csrf = readCookie('csrf_token');
  if (csrf) headers['X-CSRF-Token'] = csrf;
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: formData,
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    if (res.status === 402) {
      try {
        const detail = typeof data.detail === 'string' ? data.detail : 'Pro feature';
        window.dispatchEvent(new CustomEvent('upgrade-required', { detail: { message: detail } }));
      } catch {}
    }
    throw new Error(formatApiErrorDetail(data.detail));
  }
  return res.json();
}

export function getImageUrl(path) {
  if (!path) return null;
  if (path.startsWith('data:')) return path;
  if (path.startsWith('http')) return path;
  if (path.startsWith('/api/files/')) return `${API_URL}${path}`;
  // Storage keys (e.g. "linkmonkey/uploads/...") — if the backend has surfaced
  // a public R2 URL via window-level config we use that, else fall through to
  // the FastAPI /api/files proxy.
  const r2Public = (typeof window !== 'undefined' && window.__R2_PUBLIC_URL__)
    ? String(window.__R2_PUBLIC_URL__).replace(/\/$/, '')
    : null;
  if (r2Public) return `${r2Public}/${path}`;
  return `${API_URL}/api/files/${path}`;
}

export const api = {
  // Auth
  register: (data) => apiRequest('/api/auth/register', { method: 'POST', body: data }),
  login: (data) => apiRequest('/api/auth/login', { method: 'POST', body: data }),
  logout: () => apiRequest('/api/auth/logout', { method: 'POST' }),
  getMe: () => apiRequest('/api/auth/me'),
  refresh: () => apiRequest('/api/auth/refresh', { method: 'POST' }),
  deleteAccount: () => apiRequest('/api/auth/me', { method: 'DELETE', body: { confirm: 'DELETE' } }),
  exportMyData: () => apiRequest('/api/auth/me/export'),
  verifyEmail: (token) => apiRequest('/api/auth/verify-email', { method: 'POST', body: { token } }),
  resendVerification: () => apiRequest('/api/auth/resend-verification', { method: 'POST' }),
  completeTour: () => apiRequest('/api/onboarding/tour-complete', { method: 'POST' }),

  // Profile
  updateProfile: (data) => apiRequest('/api/profile', { method: 'PUT', body: data }),
  deleteImage: (type) => apiRequest(`/api/profile/image/${type}`, { method: 'DELETE' }),
  uploadImage: (type, file) => uploadFile(`/api/upload/${type}`, file),
  getStickerCatalog: () => apiRequest('/api/stickers/catalog'),

  // Links
  addLink: (data) => apiRequest('/api/links', { method: 'POST', body: data }),
  updateLink: (id, data) => apiRequest(`/api/links/${id}`, { method: 'PATCH', body: data }),
  uploadLinkImage: (id, file) => uploadFile(`/api/links/${id}/image`, file),
  removeLink: (id) => apiRequest(`/api/links/${id}`, { method: 'DELETE' }),
  toggleEmbed: (id, enabled) => apiRequest(`/api/links/${id}/embed`, { method: 'PATCH', body: { embed_enabled: enabled } }),
  updateSchedule: (id, start, end) => apiRequest(`/api/links/${id}/schedule`, { method: 'PATCH', body: { scheduled_start: start, scheduled_end: end } }),
  getLinks: () => apiRequest('/api/links'),
  reorderLinks: (linkIds) => apiRequest('/api/links/reorder', { method: 'PUT', body: { link_ids: linkIds } }),

  // Public
  getPublicProfile: (username) => apiRequest(`/api/public/${username}`),
  trackClick: (username, linkId) => apiRequest(`/api/public/${username}/click`, { method: 'POST', body: { link_id: linkId } }),
  
  // Analytics
  getAnalytics: () => apiRequest('/api/analytics'),

  // Subscription
  getSubscriptionStatus: () => apiRequest('/api/subscription/status'),
  verifyPayment: (sessionId) => apiRequest(`/api/subscription/verify/${sessionId}`),

  // Campaigns (Pro: email all confirmed subscribers)
  sendCampaign: (subject, body) => apiRequest('/api/campaigns', { method: 'POST', body: { subject, body } }),
  getCampaigns: () => apiRequest('/api/campaigns'),
  getCampaignCooldown: () => apiRequest('/api/campaigns/cooldown'),

  // Password Reset
  forgotPassword: (email) => apiRequest('/api/auth/forgot-password', { method: 'POST', body: { email } }),
  resetPassword: (token, newPassword) => apiRequest('/api/auth/reset-password', { method: 'POST', body: { token, new_password: newPassword } }),

  // Messaging
  sendMessage: (message) => apiRequest('/api/messages', { method: 'POST', body: { message } }),
  getMessages: () => apiRequest('/api/messages'),
  markMessagesRead: () => apiRequest('/api/messages/read', { method: 'POST' }),

  // Admin
  adminGetUsers: () => apiRequest('/api/admin/users'),
  adminToggleAffiliate: (userId) => apiRequest(`/api/admin/users/${userId}/affiliate/toggle`, { method: 'POST' }),
  adminGrantPro: (userId, cycle, action = 'grant') => apiRequest(`/api/admin/users/${userId}/pro/grant`, { method: 'POST', body: { cycle, action } }),
  adminSyncStripeUser: (userId) => apiRequest(`/api/admin/users/${userId}/stripe/sync`, { method: 'POST' }),
  adminStripeReconcile: () => apiRequest('/api/admin/stripe/reconcile', { method: 'POST' }),
  adminReplayFailedWebhooks: () => apiRequest('/api/admin/stripe/replay-failed-webhooks', { method: 'POST' }),
  adminDiagnoseAffiliate: (username) => apiRequest(`/api/admin/affiliates/diagnose/${encodeURIComponent(username)}`),
  adminPayoutsDue: () => apiRequest('/api/admin/payouts/due'),
  adminPayoutsHistory: ({ skip = 0, limit = 100 } = {}) => apiRequest(`/api/admin/payouts/history?skip=${skip}&limit=${limit}`),
  adminMarkPaid: (userId, note = '') => apiRequest(`/api/admin/payouts/${userId}/mark-paid`, { method: 'POST', body: { note } }),
  syncProFromStripe: () => apiRequest('/api/admin/stripe/sync-me', { method: 'POST' }),
  myRecentTips: () => apiRequest('/api/me/tips'),
  adminAuditLog: ({ skip = 0, limit = 50 } = {}) => apiRequest(`/api/admin/audit?skip=${skip}&limit=${limit}`),
  adminTraffic: () => apiRequest('/api/admin/traffic'),
  adminNudgeInsights: () => apiRequest('/api/admin/nudge-insights'),
  submitReview: (rating, text) => apiRequest('/api/reviews', { method: 'POST', body: { rating, text } }),
  getMyReview: () => apiRequest('/api/reviews/mine'),
  getPublicReviews: () => apiRequest('/api/public/reviews'),
  adminReviews: () => apiRequest('/api/admin/reviews'),
  adminReviewAction: (id, action) => apiRequest(`/api/admin/reviews/${id}/${action}`, { method: 'POST' }),
  adminEmailUsers: (payload) => apiRequest('/api/admin/email-users', { method: 'POST', body: payload }),
  trackSiteVisit: (page = 'home') => apiRequest('/api/public/site-visit', { method: 'POST', body: { page } }),
  adminGetMessages: () => apiRequest('/api/admin/messages'),
  adminMarkRead: (userId) => apiRequest(`/api/admin/messages/${userId}/read`, { method: 'POST' }),
  adminReply: (userId, message) => apiRequest(`/api/admin/messages/${userId}/reply`, { method: 'POST', body: { message } }),
  adminGetStats: () => apiRequest('/api/admin/stats'),

  // Diary
  getDiary: () => apiRequest('/api/diary'),
  createDiaryPost: (data) => apiRequest('/api/diary', { method: 'POST', body: data }),
  uploadDiaryImage: (postId, file) => uploadFile(`/api/diary/${postId}/image`, file),
  deleteDiaryPost: (id) => apiRequest(`/api/diary/${id}`, { method: 'DELETE' }),
  getPublicDiary: (username) => apiRequest(`/api/public/${username}/diary`),

  // Referrals
  getReferrals: () => apiRequest('/api/referrals'),
  getPayoutHistory: () => apiRequest('/api/referrals/payout-history'),
  updatePayoutInfo: (data) => apiRequest('/api/referrals/payout', { method: 'PUT', body: data }),
  adminGetReferrals: () => apiRequest('/api/admin/referrals'),

  // Subscribers
  getSubscribers: (skip = 0, limit = 5, q = '') => apiRequest(`/api/subscribers?skip=${skip}&limit=${limit}${q ? `&q=${encodeURIComponent(q)}` : ''}`),
  publicSubscribe: (username, email) => apiRequest(`/api/public/${username}/subscribe`, { method: 'POST', body: { email } }),

  // Links
  updateCategory: (id, category) => apiRequest(`/api/links/${id}/category`, { method: 'PATCH', body: { category } }),

  // Affiliates
  applyAffiliate: (data) => apiRequest('/api/affiliates/apply', { method: 'POST', body: data }),
  getAffiliateStatus: () => apiRequest('/api/affiliates/status'),
  getAffiliateDashboard: () => apiRequest('/api/affiliates/dashboard'),
  adminGetAffiliates: () => apiRequest('/api/admin/affiliates'),
  adminApproveAffiliate: (id) => apiRequest(`/api/admin/affiliates/${id}/approve`, { method: 'POST' }),
  adminRejectAffiliate: (id) => apiRequest(`/api/admin/affiliates/${id}/reject`, { method: 'POST' }),

  // Stripe
  createCheckout: (plan) => apiRequest('/api/stripe/checkout', { method: 'POST', body: { plan, origin_url: window.location.origin } }),
  getCheckoutStatus: (sessionId) => apiRequest(`/api/stripe/status/${sessionId}`),
  createPortalSession: () => apiRequest('/api/stripe/portal', { method: 'POST', body: { return_url: `${window.location.origin}/billing` } }),
  startTrial: () => apiRequest('/api/trial/start', { method: 'POST' }),

  // Billing
  getBilling: () => apiRequest('/api/billing/me'),

  // Payout
  getPayoutInfo: () => apiRequest('/api/payout-info'),
  updatePayoutInfo: (data) => apiRequest('/api/payout-info', { method: 'PUT', body: data })
};
